import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import Header from "../../components/Header";
import AddButton from "../../components/AddButton";
import DeleteModal from "../../components/DeleteModal";
import Pagination from "../../components/Pagination";
import Loading from "../../components/Loading";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import {
  getTrainerPrograms,
  deleteTrainerProgram,
  setTrainerProgramStatus,
  getTrainerProgramParticipantsCount,
  TrainerProgram,
} from "../../services/trainerProgramService";
import { getTrainers, Trainer } from "../../services/trainerService";

const DAY_LABELS: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const TrainerProgramsPage: React.FC = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<TrainerProgram[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<TrainerProgram | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [trainerFilter, setTrainerFilter] = useState<number | "">("");
  const [participantCounts, setParticipantCounts] = useState<Record<number, number>>({});

  useEffect(() => {
    getTrainers()
      .then(setTrainers)
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTrainerPrograms(
        currentPage,
        10,
        trainerFilter
      );
      setData(response.programs);
      setTotalPages(response.totalPages);
    } catch {
      setError("Failed to fetch Trainer Programs");
    } finally {
      setLoading(false);
    }
  }, [currentPage, trainerFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const fetchCounts = async () => {
      const map: Record<number, number> = {};
      for (const program of data) {
        try {
          map[program.id] = await getTrainerProgramParticipantsCount(program.id);
        } catch {
          map[program.id] = 0;
        }
      }
      setParticipantCounts(map);
    };
    fetchCounts();
  }, [data]);

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await deleteTrainerProgram(itemToDelete.id);
      setSuccessMessage("Program deleted successfully");
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch {
      setError("Failed to delete program");
    }
  };

  const getDaySummary = (program: TrainerProgram) => {
    const days = Array.from(new Set(program.workouts.map((w) => w.dayOfWeek).filter(Boolean)));
    return days.map((d) => DAY_LABELS[d!] ?? d).join(", ") || "—";
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Trainer Programs (New)" />

      <div className="my-4 flex justify-between items-center">
        <select
          className="border p-2 rounded"
          value={trainerFilter}
          onChange={(e) =>
            setTrainerFilter(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">All Trainers</option>
          {trainers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        <AddButton
          text="Add Program"
          onClick={() => navigate("/add-trainer-program")}
        />
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
        />
      )}
      {error && (
        <ErrorMessage message={error} onClose={() => setError(null)} />
      )}

      {loading ? (
        <Loading message="Loading..." />
      ) : (
        <>
          <div className="rounded-lg overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-left">Title</th>
                  <th className="py-2 px-4 text-left">Trainer</th>
                  <th className="py-2 px-4 text-left">Days</th>
                  <th className="py-2 px-4 text-left">Workouts</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Participants</th>
                  <th className="py-2 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">{item.id}</td>
                    <td className="py-3 px-4 font-medium">{item.title}</td>
                    <td className="py-3 px-4">
                      {item.trainer?.name ?? "—"}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {getDaySummary(item)}
                    </td>
                    <td className="py-3 px-4">{item.workouts.length}</td>
                    <td className="py-3 px-4">
                      <label className="flex items-center cursor-pointer gap-2">
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          onChange={async (e) => {
                            try {
                              await setTrainerProgramStatus(
                                item.id,
                                e.target.checked
                              );
                              setSuccessMessage("Status updated");
                              fetchData();
                            } catch {
                              setError("Failed to update status");
                            }
                          }}
                        />
                        {item.isActive ? "Active" : "Inactive"}
                      </label>
                    </td>
                    <td className="py-3 px-4">
                      {participantCounts[item.id] ?? "..."}
                    </td>
                    <td className="py-3 px-4 flex gap-2">
                      <button
                        onClick={() =>
                          navigate("/edit-trainer-program", { state: item })
                        }
                        className="px-2 py-1 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setItemToDelete(item);
                          setIsDeleteModalOpen(true);
                        }}
                        className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400">
                      No programs yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={itemToDelete?.title ?? ""}
        itemType="program"
      />
    </div>
  );
};

export default TrainerProgramsPage;
